import React from 'react';
import { useLocation } from 'react-router';
import CustomTextField from '../Input/CustomTextField';
import HospitalOrInstitutions from 'components/Global/HospitalOrInstitutions';
import CustomSelect from 'components/Input/CustomSelect';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import PasswordTextField from 'components/Input/PasswordTextField';
import UploadFileInput from 'components/uploadFile/uploadFileInput';
import { Grid, Typography } from '@material-ui/core';
import UploadDisplay from 'components/uploadFile/UploadDisplay';
import ClusterSelection from 'components/ClusterSelection';
import CustomCheckbox from 'components/CustomCheckbox';
import { isArray } from 'lodash';
import CustomRadio from 'components/CustomRadio';

/**
 * @description: Mapping TextField
 * @param {Object} props
 * @param {Object} props.item
 * @param {Function} props.handleFormData textFiled onChange
 * @param {Boolean} props.isAuthorizedEmail Autocomplete freeSolo
 * @param {Boolean} props.multiple upload multiple
 * @return {Element} return an element
 */
const MapTextField = (props) => {
    const {
        item,
        handleFormData,
        isAuthorizedEmail = true,
        multiple,
        isNoSpace,
        isTrim,
        confirmPasswordNotMatchPassword,
        forceErrorDisplay,
        isValidValue = () => null,
        minLength,
        maxLength,
        freeSolo,
        onRemove,
        showErrorWhenOnBlur,
        localTime,
        ...rest
    } = props;

    const location = useLocation();
    const isAmendmentForm = location?.pathname?.includes('application-amendment-detail');
    const isCVFile = item.label === 'CV File';

    let extra = {};

    if (item.validators?.length) {
        extra = { validators: item.validators };
    }

    let html = null;
    switch (item.type) {
        case 'radio':
            html = (
                <Grid container>
                    <CustomRadio
                        onChange={(e) => {
                            handleFormData(
                                {
                                    target: {
                                        name: item.name,
                                        value: item.returnTrueFalse ? JSON.parse(e.target.value) : e.target.value,
                                    },
                                },
                                item.type,
                            );
                        }}
                        value={item.value}
                        operators={item.operators}
                        {...rest}
                        {...extra}
                    />
                    {item.validators.includes('isRequired') && item.value === '' && forceErrorDisplay ? (
                        <Grid
                            container
                            style={{
                                marginTop: 8,
                            }}
                        >
                            {isValidValue(false)}
                            <Typography color={forceErrorDisplay ? 'error' : 'inherit'}>
                                At least one of the option should be selected.
                            </Typography>
                        </Grid>
                    ) : (
                        <>{isValidValue(true)}</>
                    )}
                </Grid>
            );
            break;
        case 'checkbox':
            html = (
                <Grid container>
                    <CustomCheckbox
                        onItemChange={(key, checked) => {
                            handleFormData(
                                {
                                    target: {
                                        name: item.name,
                                        value: key,
                                    },
                                },
                                item.type,
                            );
                        }}
                        value={[item.value]}
                        defaultData={item.operators}
                        {...rest}
                        {...extra}
                    />
                    {item.validators.includes('isRequired') && item.value === '' && forceErrorDisplay ? (
                        <Grid
                            container
                            style={{
                                marginTop: 8,
                            }}
                        >
                            {isValidValue(false)}
                            <Typography color={forceErrorDisplay ? 'error' : 'inherit'}>
                                At least one of the option should be selected.
                            </Typography>
                        </Grid>
                    ) : (
                        <>{isValidValue(true)}</>
                    )}
                </Grid>
            );
            break;
        case 'boolean':
            html = (
                <div style={{ display: 'flex', height: 37, alignItems: 'center' }}>
                    <CustomCheckbox
                        defaultData={[
                            { key: true, value: false, display: item.trueValue ?? 'Yes' },
                            { key: false, value: false, display: item.falseValue ?? 'No' },
                        ]}
                        isValidValue={isValidValue}
                        forceErrorDisplay={forceErrorDisplay}
                        name={item.name}
                        value={[item.value]}
                        radioButton
                        isRequired={item.isRequired}
                        onChange={(key, value) => {
                            const selected = value.find((item) => item.value === true);
                            handleFormData(
                                {
                                    target: {
                                        name: key,
                                        value: selected.key,
                                    },
                                },
                                item.type,
                            );
                        }}
                        {...rest}
                        {...extra}
                    />
                </div>
            );
            break;
        case 'input':
            html = (
                <CustomTextField
                    showErrorWhenOnBlur={
                        item.showErrorWhenOnBlur !== undefined ? item.showErrorWhenOnBlur : showErrorWhenOnBlur
                    }
                    minNumber={item.minNumber}
                    maxNumber={item.maxNumber}
                    isTextArea={item.isTextArea}
                    textAreaMinRow={item.textAreaMinRow}
                    tooltip={item.tooltip}
                    disabled={item.disabled}
                    key={item.validators}
                    label={item.label}
                    name={item.name}
                    value={item.value}
                    onChange={(e) => handleFormData(e, item.type)}
                    isNoSpace={isNoSpace}
                    isTrim={item.isTrim ?? isTrim}
                    isValidValue={isValidValue}
                    forceErrorDisplay={forceErrorDisplay}
                    minLength={item.minLength ?? minLength}
                    maxLength={item.maxLength ?? maxLength}
                    allowAction={item.allowAction}
                    {...extra}
                    {...rest}
                />
            );
            break;
        case 'cluster':
            html = (
                <ClusterSelection
                    style={{ width: '100%' }}
                    name={item.name}
                    value={item.value}
                    disableOtherOption={item.disableOtherOption}
                    onChange={(e) => {
                        item?.onChange?.(e);
                        handleFormData(e, item.type);
                    }}
                    isValidValue={isValidValue}
                    forceErrorDisplay={forceErrorDisplay}
                    isRequired={item.isRequired}
                    disabled={item.disabled}
                    {...rest}
                    {...extra}
                />
            );
            break;
        case 'select':
            html = (
                <CustomSelect
                    fullWidth
                    options={item.operators}
                    label={item.display ?? item.label}
                    name={item.name}
                    disabled={item.disabled}
                    value={item.value}
                    setValue={(e) => handleFormData(e, item.type)}
                    isValidValue={isValidValue}
                    forceErrorDisplay={forceErrorDisplay}
                    isNoneSelect={item.isNoneSelect || false}
                    {...extra}
                    {...rest}
                />
            );
            break;
        case 'hospitalOrInstitution':
            if (item.allowClusters) {
                extra.allowClusters = item.allowClusters;
            }

            html = (
                <HospitalOrInstitutions
                    isAuthorizedEmail={item.freeSolo ? false : isAuthorizedEmail}
                    hospitalOrInstitution={item.value}
                    label={item.label}
                    name={item.name}
                    setHospitalOrInstitution={(e) => handleFormData(e, item.type)}
                    isValidValue={isValidValue}
                    forceErrorDisplay={forceErrorDisplay}
                    minLength={item.minLength ?? minLength}
                    maxLength={item.maxLength ?? maxLength}
                    disabled={item.disabled}
                    showOtherOption={item.showOtherOption}
                    {...extra}
                    {...rest}
                />
            );
            break;
        case 'renderCell':
            html = item.renderCell ? item.renderCell() : null;
            break;
        case 'date':
            html = (
                <CustomDatePicker
                    name={item.name}
                    value={item.value}
                    label={item.label}
                    handleChangeDate={(data) => handleFormData({ ...data, target: { ...data.target } }, item.type)}
                    isValidValue={isValidValue}
                    forceErrorDisplay={forceErrorDisplay}
                    localTime={item.localTime ?? localTime}
                    {...extra}
                    {...rest}
                />
            );
            break;
        case null:
            html = null;
            break;
        case 'password':
            html = (
                <PasswordTextField
                    label={item.label}
                    name={item.name}
                    value={item.value}
                    handleChange={(e) => handleFormData(e, item.type)}
                    isValidValue={isValidValue}
                    forceErrorDisplay={forceErrorDisplay}
                    confirmPasswordNotMatchPassword={confirmPasswordNotMatchPassword}
                    minLength={item.minLength ?? minLength}
                    maxLength={item.maxLength ?? maxLength}
                    {...extra}
                    {...rest}
                />
            );
            break;
        case 'uploadFile':
            html = (
                <Grid container>
                    {isArray(item.value) && !(item.showFileSize && item.value.length >= item.showFileSize) && (
                        <UploadFileInput
                            inputStyle={item.inputStyle}
                            multiple={item.multiple}
                            name={item.name}
                            fileOnChange={(list) =>
                                handleFormData({ target: { list: list, name: item.name } }, item.type)
                            }
                            label={''}
                            {...rest}
                        />
                    )}
                    {item.value.length ? (
                        <Grid container style={{ margin: '8px 8px 0 0' }}>
                            {isValidValue(true)}
                            <UploadDisplay
                                fileList={item.value}
                                enableRemove={item.enableRemove}
                                onRemove={(index) => onRemove(index, item)}
                            />
                        </Grid>
                    ) : (
                        <Grid
                            container
                            style={{
                                marginTop: 8,
                            }}
                        >
                            {isValidValue(false)}
                            <Typography color={forceErrorDisplay ? 'error' : 'inherit'}>
                                No file has uploaded!
                            </Typography>
                        </Grid>
                    )}
                    {!isCVFile && isAmendmentForm && (item.showRed || item.showRed === undefined) && (
                        <Typography color="textSecondary" style={{ marginTop: 4, color: '#FF0000' }}>
                            * Clean and track version of documents are required to upload (except newly-submitted
                            document) for Amendment Application
                        </Typography>
                    )}
                </Grid>
            );
            break;
        default:
            html = (
                <Grid container alignItems="center">
                    {item.value}
                </Grid>
            );
            break;
    }
    return html;
};
export default MapTextField;
